import React, {
  FC,
  ReactEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import useWidth from "../hooks/useWidth";
import useVideoURL from "../hooks/useVideoURL";
import Section from "./Section";
import { Stack } from "@mui/material";
import Image from "./Image";
import { VideoProps } from "./Video";

export interface VideoAnimationProps extends VideoProps {
  videoPlaceholder?: string;
}

const videoIsPlayingStyle = {
  ".image-wrapper": {
    opacity: 0,
  },
  ".video-wrapper video": {
    zIndex: 12,
  },
};

/**
 * A functional component that displays a video. The video file to be displayed is determined based on the screen width.
 * The process of compressing the video files is a manual process. The video files are compressed using the Handbrake application.
 * If in the future we conclude on optimal settings that work for all videos, we can automate the process.
 *
 * @param {string} largeScreenVideo - The URL for the video file to be displayed on large screens.
 * @param {string} [largeScreenVideoWebm] - The URL for the video file in the WebM format to be displayed on large screens.
 * @param {string} smallScreenVideo - The URL for the video file to be displayed on small screens.
 * @param {string} [videoPlaceholder=""] - Image file name for a video placeholder.
 * @param {boolean} [fullHeight=false] - Determines whether the video should fill all the height.
 * @param {boolean} [controls=false] - Determines whether the video player controls should be displayed.
 * @param {object} style - Video element style.
 */

const VideoAnimation: FC<VideoAnimationProps> = ({
  largeScreenVideo,
  largeScreenVideoWebm,
  smallScreenVideo,
  videoPlaceholder,
  controls = false,
  fullHeight = false,
  style = {},
}) => {
  const [source, setSource] = useState(largeScreenVideoWebm);
  const width = useWidth();

  useEffect(() => {
    if (width === "xs") {
      setSource(smallScreenVideo);
    } else {
      setSource(largeScreenVideo);
    }
  }, [width, largeScreenVideoWebm, smallScreenVideo]);
  const originalURL = useVideoURL(largeScreenVideo);
  const sourceURL = useVideoURL(source);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  const onMouseOver: ReactEventHandler = useCallback(() => {
    if (!videoRef.current) {
      return;
    }

    if (videoRef.current.currentTime !== 0) {
      return;
    }

    videoRef.current.play();
    setIsVideoPlaying(true);
  }, [videoRef]);

  return (
    <Section
      onFocus={onMouseOver}
      onMouseOver={onMouseOver}
      height={fullHeight ? "100%" : "auto"}
      sx={{
        position: "relative",
      }}
    >
      <Stack className="video-wrapper" height={fullHeight ? "100%" : "auto"}>
        <video
          ref={videoRef}
          key={sourceURL}
          autoPlay={width === "xs"}
          onEnded={() => {
            videoRef.current!.currentTime = 0;
            setIsVideoPlaying(false);
          }}
          loop={width === "xs"}
          muted
          preload="auto"
          controls={controls}
          playsInline
          style={{
            width: "100%",
            height: "100%",
            display: "block",
            objectFit: "cover",
            margin: 0,
            marginBottom: -1,
            zIndex: width === "xs" || isVideoPlaying ? 12 : 10,
            ...style,
          }}
        >
          {sourceURL && <source src={sourceURL} />}
          {originalURL && <source src={originalURL} />}
        </video>
      </Stack>
      <Stack
        className="image-wrapper"
        sx={{
          transition: "opacity 400ms cubic-bezier(0.14, 0.53, 0.04, 1.01)",
          zIndex: 11,
          opacity: width === "xs" || isVideoPlaying ? 0 : 1,
          position: "absolute",
          top: 0,
          display: videoPlaceholder ? "block" : "none",
        }}
        onMouseOver={onMouseOver}
      >
        <Image
          name={videoPlaceholder ?? ""}
          alt="Video placeholder"
          style={{
            height: "100%",
          }}
        />
      </Stack>
    </Section>
  );
};

export default VideoAnimation;
